import React, { useState } from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { Card, Button } from 'reactstrap'
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { OverlineTitle } from '../text/Text'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { NavLink } from 'react-router-dom'
import { CheckCircle } from '@mui/icons-material'
import { Typography, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { OAuth2 } from '../../services/McDataServices'
import { prodFrontend } from '../../services/api-common'

const GoogleIcon = ({ size = 20 }) => (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    style={{ width: `${size}px`, height: `${size}px`, display: 'block' }}
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
)

export const MarketCalculusCard = ({ id, className, bodyClass, ...props }) => {
  return (
    <Card id={id} className={`card-bordered ${className || ''}`}>
      <div className={`card-inner ${bodyClass || ''}`}>{props.children}</div>
    </Card>
  )
}
export const MarketCalculusLinkCard = ({
  link,
  className,
  bodyClass,
  disabled,
  ...props
}) => {
  return (
    <NavLink to={link} className={disabled ? 'menu-disabled-link' : ''}>
      <Card className={`card-bordered center py-5 dashboard-menu ${className ||
      ''}`}>
        <div className={`card-inner ${bodyClass || ''}`}>{props.children}</div>
      </Card>
    </NavLink>
  )
}

export const GmailConnectButton = ({ langCode, email, connected = false, onDisconnect }) => {
  const { t } = useTranslation()

  const connectGmail = async () => {
    const clientId = '1044629332572-a51l0ee1dbpebirgco4goqhvon319k7m.apps.googleusercontent.com'
    const redirectUri = `${prodFrontend}/${langCode}/campaigns`
    const scope = 'https://mail.google.com/+https://www.googleapis.com/auth/userinfo.email'
    const responseType = 'code'
    const accessType = 'offline' // to get a refresh_token

    const stateFromBackend = await OAuth2.Gmail()
    const state = encodeURIComponent(stateFromBackend)

    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&access_type=${accessType}&prompt=consent&state=${state}`
    window.location.href = oauthUrl
  }

  if (connected) {
    return (
      <Box
        className="flex items-center border"
        sx={{
          backgroundColor: '#f2f2f2',
          borderColor: '#e9ecef',
          borderRadius: '20px',
          padding: '8px 12px',
          gap: '12px',
          width: 'fit-content'
        }}
      >
        <Box className="flex items-center gap-2">
          <GoogleIcon size={20} />
          <Box>
            <Typography
              variant="body2"
              className="flex items-center"
              sx={{
                color: '#212529',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '2px',
                gap: '8px'
              }}
            >
                {t('mailTemplate.users.modal.connectedWithGmail')}
              <CheckCircle sx={{ color: '#28a745', fontSize: '16px' }} />
            </Typography>
            {email && (
              <Typography
                variant="body2"
                sx={{
                  color: '#6c757d',
                  fontSize: '13px',
                  fontWeight: 400
                }}
              >
                {email}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className="flex items-center">
          {/* {onDisconnect && (
            <IconButton
              onClick={onDisconnect}
              size="small"
              className="text-gray-500 hover:text-red-500 hover:bg-gray-100"
              title={t('mailTemplate.users.modal.disconnectGmail')}
            >
              <Close fontSize="small" />
            </IconButton>
          )} */}
        </Box>
      </Box>
    )
  }

  return (
    <Button
      size="sm"
      className="gsi-material-button"
      onClick={connectGmail}
      type="button"
    >
      <div className="gsi-material-button-state"></div>
      <div className="gsi-material-button-content-wrapper">
        <div className="gsi-material-button-icon">
          <GoogleIcon />
        </div>
        <span
          className="gsi-material-button-contents">{t('mailTemplate.users.modal.signInWithGoogle')}</span>
        <span style={{ display: 'none' }}>{t('mailTemplate.users.modal.signInWithGoogle')}</span>
      </div>
    </Button>
  )
}

export const PreviewCard = ({ className, bodyClass, ...props }) => {
  return (
    <Card className={`card-preview ${className || ''}`}>
      <div className={`card-inner ${bodyClass || ''}`}>{props.children}</div>
    </Card>
  )
}

export const PreviewAltCard = ({ className, bodyClass, ...props }) => {
  return (
    <Card className={`card-bordered ${className || ''}`}>
      <div className={`card-inner ${bodyClass || ''}`}>{props.children}</div>
    </Card>
  )
}

export const PreviewTable = ({ className, ...props }) => {
  return (
    <Card className={`card-preview ${className || ''}`}>
      <table className={`table preview-reference ${props.size
        ? `table-${props.size}`
        : ''}`}>{props.children}</table>
    </Card>
  )
}
export const CodeBlock = ({ language, ...props }) => {
  const [copyText] = useState(props.children)
  const [copyState, setCopyState] = useState(false)
  const onCopyClick = () => {
    setCopyState(true)
    setTimeout(() => setCopyState(false), 2000)
  }
  return (
    <div className={`code-block code-block-clean ${copyState
      ? 'clipboard-success'
      : ''}`}>
      <OverlineTitle className="title">{props.title
        ? props.title
        : 'Code Example'}</OverlineTitle>
      <CopyToClipboard text={copyText} onCopy={onCopyClick}>
        <Button color="blank" size="sm" className="clipboard-init">
          {copyState ? 'Copied' : 'Copy'}
        </Button>
      </CopyToClipboard>
      <SyntaxHighlighter language="javascript"
                         className="bg-lighter h-max-150px m-0"
                         style={a11yLight}>
        {props.children}
      </SyntaxHighlighter>
    </div>
  )
}
